import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { getSharedReport } from '../lib/api';
import { formatNumber, getMaterialLines } from '../lib/utils';
import { MapPin, Ruler, Package, FileText, Phone, Building2, Loader2, ArrowRight, Check, Shield } from 'lucide-react';
import { InfoTooltip, METRIC_TOOLTIPS } from '../components/InfoTooltip';

export default function SharedReport() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedReport();
  }, [shareToken]);

  const loadSharedReport = async () => {
    try {
      const response = await getSharedReport(shareToken);
      setReport(response.data);
    } catch (err) {
      setError('This report link is no longer available.');
    } finally {
      setLoading(false);
    }
  };

  const roofComplexityNames = {
    'flat_low': 'Flat/Low Slope',
    'simple_gable': 'Simple Gable',
    'moderate_hip': 'Moderate Hip',
    'complex_mansard': 'Complex Mansard',
    'multi_facet': 'Multi-Facet'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="text-center max-w-md">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl text-slate-900 uppercase mb-2">Report Not Found</h1>
          <p className="text-slate-600 mb-6">{error || 'This shared report link may have expired.'}</p>
          <Button onClick={() => navigate('/')} className="bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold uppercase">
            Visit SquareCount.io
          </Button>
        </div>
      </div>
    );
  }

  const mat = report.materials || {};
  const gutters = mat.gutters || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shared-report-page">
      <Helmet>
        <title>Roof Measurement Report — {report.address || 'Property'} | SquareCount</title>
        <meta name="description" content={`Professional roof measurement report for ${report.address}. ${formatNumber(report.total_area_sqft)} sq ft total area, pitch ${report.pitch}/12.`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="bg-slate-900 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">SC</span>
            </div>
            <span className="text-white font-heading font-bold tracking-wide">SQUARECOUNT</span>
          </div>
          {report.company_name && (
            <div className="text-right">
              <p className="text-white font-heading font-bold text-sm">{report.company_name}</p>
              {report.company_phone && (
                <p className="text-slate-400 text-xs flex items-center gap-1 justify-end">
                  <Phone className="w-3 h-3" /> {report.company_phone}
                </p>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Report */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 uppercase mb-2">
            Roof Measurement Report
          </h1>
          <p className="text-slate-500 flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5 text-orange-500" />
            {report.address}
          </p>
        </div>

        {/* Measurement Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="shared-report-measurements">
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
            <p className="text-xs font-mono uppercase text-slate-500 mb-1 flex items-center gap-1">
              Total Area <InfoTooltip text={METRIC_TOOLTIPS.totalArea} />
            </p>
            <p className="text-2xl font-heading font-bold text-orange-500">
              {formatNumber(report.total_area_sqft)} <span className="text-sm font-body text-slate-400">sq ft</span>
            </p>
          </div>
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
            <p className="text-xs font-mono uppercase text-slate-500 mb-1 flex items-center gap-1">
              Squares <InfoTooltip text={METRIC_TOOLTIPS.squares} />
            </p>
            <p className="text-2xl font-heading font-bold text-slate-900">
              {(report.total_area_sqft / 100).toFixed(1)}
            </p>
          </div>
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
            <p className="text-xs font-mono uppercase text-slate-500 mb-1 flex items-center gap-1">
              Pitch <InfoTooltip text={METRIC_TOOLTIPS.pitch} />
            </p>
            <p className="text-2xl font-heading font-bold text-slate-900">
              {report.pitch}/12
            </p>
          </div>
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
            <p className="text-xs font-mono uppercase text-slate-500 mb-1 flex items-center gap-1">
              Perimeter <InfoTooltip text={METRIC_TOOLTIPS.perimeter} />
            </p>
            <p className="text-2xl font-heading font-bold text-slate-900">
              {formatNumber(report.perimeter_ft)} <span className="text-sm font-body text-slate-400">ft</span>
            </p>
          </div>
        </div>

        {/* Materials Table */}
        <div className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden mb-8" data-testid="shared-report-materials">
          <div className="bg-slate-900 px-6 py-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-500" />
            <h2 className="font-heading font-bold text-lg text-white uppercase">Material Estimates</h2>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500">Material</th>
                <th className="text-right px-6 py-3 text-xs font-mono uppercase text-slate-500">Quantity</th>
                <th className="text-right px-6 py-3 text-xs font-mono uppercase text-slate-500 hidden md:table-cell">Unit</th>
              </tr>
            </thead>
            <tbody>
              {getMaterialLines(mat).map((line) => (
                <tr key={line.key} className="border-b border-slate-100 last:border-0">
                  <td className="px-6 py-3 text-sm text-slate-700">{line.label}</td>
                  <td className="px-6 py-3 text-right font-mono font-bold text-slate-900">{line.qty || 0}</td>
                  <td className="px-6 py-3 text-right text-sm text-slate-500 hidden md:table-cell">{line.unit}</td>
                </tr>
              ))}
              {mat.custom_items && mat.custom_items.length > 0 && (
                <>
                  <tr className="bg-orange-50 border-b border-slate-100">
                    <td colSpan={3} className="px-6 py-2 text-xs font-heading font-bold uppercase text-orange-600">Custom Materials</td>
                  </tr>
                  {mat.custom_items.map((item, idx) => (
                    <tr key={`ci-${idx}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-6 py-3 text-sm text-slate-700">{item.name}</td>
                      <td className="px-6 py-3 text-right font-mono font-bold text-slate-900">{item.quantity}</td>
                      <td className="px-6 py-3 text-right text-sm text-slate-500 hidden md:table-cell">{item.unit || ''}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Gutter Materials - Only shown when include_gutters is true */}
        {report.include_gutters && gutters.linear_ft > 0 && (
          <div className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden mb-8" data-testid="shared-report-gutters">
            <div className="bg-slate-900 px-6 py-4 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-orange-500" />
              <h2 className="font-heading font-bold text-lg text-white uppercase">Gutter Materials</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500">Material</th>
                  <th className="text-right px-6 py-3 text-xs font-mono uppercase text-slate-500">Quantity</th>
                  <th className="text-right px-6 py-3 text-xs font-mono uppercase text-slate-500 hidden md:table-cell">Unit</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Gutter Sections (10ft)', gutters.sections_10ft, 'sections'],
                  ['Total Linear Feet', gutters.linear_ft, 'ft'],
                  ['Downspouts', gutters.downspouts, 'pcs'],
                  ['Downspout Elbows', gutters.downspout_elbows, 'pcs'],
                  ['End Caps', gutters.end_caps, 'pcs'],
                  ['Inside Corners', gutters.inside_corners, 'pcs'],
                  ['Outside Corners', gutters.outside_corners, 'pcs'],
                  ['Hangers', gutters.hangers, 'pcs'],
                  ['Gutter Screws', gutters.gutter_screws_lbs, 'lbs'],
                  ['Splash Blocks', gutters.splash_blocks, 'pcs'],
                  ['Gutter Sealant', gutters.gutter_sealant_tubes, 'tubes'],
                ].map(([name, qty, unit]) => (
                  <tr key={name} className="border-b border-slate-100 last:border-0">
                    <td className="px-6 py-3 text-sm text-slate-700">{name}</td>
                    <td className="px-6 py-3 text-right font-mono font-bold text-slate-900">{qty || 0}</td>
                    <td className="px-6 py-3 text-right text-sm text-slate-500 hidden md:table-cell">{unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Linear Measurements (valleys/ridges, reference) */}
        {Array.isArray(report.line_measurements) && report.line_measurements.length > 0 && (
          <div className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden mb-8" data-testid="shared-report-lines">
            <div className="bg-slate-900 px-6 py-4 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-orange-500" />
              <h2 className="font-heading font-bold text-lg text-white uppercase">Linear Measurements</h2>
            </div>
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase text-slate-500">Name</th>
                  <th className="text-right px-6 py-3 text-xs font-mono uppercase text-slate-500">Linear Feet</th>
                </tr>
              </thead>
              <tbody>
                {report.line_measurements.map((l, idx) => (
                  <tr key={`lm-${idx}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-6 py-3 text-sm text-slate-700 capitalize">{l.type}</td>
                    <td className="px-6 py-3 text-sm text-slate-700">{l.name}</td>
                    <td className="px-6 py-3 text-right font-mono font-bold text-slate-900">{(Number(l.length_ft) || 0).toFixed(1)} ft</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-6 py-3 text-xs text-slate-400">Reference measurements (valleys &amp; ridges) &mdash; not included in material quantities.</p>
          </div>
        )}

        {/* Report Details */}
        <div className="bg-white border-2 border-slate-200 rounded-lg p-6 mb-8">
          <h3 className="font-heading font-bold text-sm uppercase text-slate-500 mb-3">Report Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Waste Factor</p>
              <p className="font-bold text-slate-900">{Math.round((report.waste_factor || 0.10) * 100)}%</p>
            </div>
            <div>
              <p className="text-slate-500">Roof Complexity</p>
              <p className="font-bold text-slate-900">{roofComplexityNames[report.roof_complexity] || 'Simple Gable'}</p>
            </div>
            <div>
              <p className="text-slate-500">Generated By</p>
              <p className="font-bold text-slate-900">{report.company_name || 'SquareCount.io'}</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4">
            * Estimates include waste factor. All quantities should be verified on-site before ordering.
          </p>
        </div>

        {/* CTA for homeowner */}
        <div className="bg-slate-900 rounded-lg p-6 md:p-8 text-center" data-testid="shared-report-cta">
          <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">
            Need Roof Measurements?
          </h2>
          <p className="text-slate-400 mb-6 max-w-lg mx-auto">
            Get instant satellite roof measurements with professional material lists. 
            Trusted by roofing contractors nationwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-orange-500 hover:bg-orange-400 text-white h-12 px-8 font-heading font-bold uppercase tracking-wide"
              data-testid="shared-report-signup-btn"
            >
              Start Your Free 7-Day Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-slate-500 text-xs">
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" /> Satellite imagery</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" /> Instant results</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" /> Cancel anytime</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 mt-12 py-6 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-slate-500 text-sm">
            Powered by <a href="/" className="text-orange-500 hover:text-orange-400 font-bold">SquareCount.io</a> — Professional Roof Measurement Software
          </p>
        </div>
      </footer>
    </div>
  );
}
